import React, { useContext } from 'react'
import { List, ListItem, ListItemText, ListItemIcon, Badge, IconButton, CircularProgress, ListItemSecondaryAction, Typography } from '@mui/material'
import makeStyles from '@mui/styles/makeStyles';
import NotificationsIcon from '@mui/icons-material/Notifications'
import GetAppIcon from '@mui/icons-material/GetApp'
import CancelIcon from '@mui/icons-material/Cancel'
import DoneIcon from '@mui/icons-material/Done'
import { MessageContext } from './Contexts'

const useStyles = makeStyles(theme => ({
  root: {
    position: 'fixed',
    top: '50px',
    right: '5%',
    width: 400,
    background: theme.palette.background.paper,
    color: theme.palette.text.primary,
    boxShadow: theme.shadows[1],
    borderRadius: theme.shape.borderRadius,
  },
  badge: {
    color: theme.palette.background.paper,
  },
  info: {
    display: 'block',
  },
  warnLink: {
    color: 'rgb(26,107,173)',
    '&:active': {
      color: 'rgb(255,155,0)',
    }
  }
}))

const toKb = (number) => {
  const kb = Math.round(number/1000);
  return kb.toLocaleString()
}

const toPct = (fraction) => {
    if (fraction === 0) {
        return "0%"
    } else {
        return (fraction * 100).toFixed(1) + "%"
    }
}

export default function NotificationBar({ notifications }) {
  const classes = useStyles()
  const { messageDispatch, notificationBarOpen, setNotificationBarOpen } = useContext(MessageContext)

  const download = link => () => (link ? (window.location = link) : null)

  return (<>
    <IconButton onClick={() => setNotificationBarOpen(!notificationBarOpen)} size="large">
      <Badge badgeContent={notifications.length} color='error' className={classes.badge}>
        <NotificationsIcon />
      </Badge>
    </IconButton>
    {notificationBarOpen && notifications && notifications.length > 0 && (
      <div className={classes.root}>
        <List component='nav'>
          {notifications.map(
            ({
              operation_id,
              data_room_display_name,
              stage_label,
              stage,
              download_link,
              number_files,
              number_files_skipped,
              number_files_sw,
              number_files_redacting,
              size,
              size_dl,
              number_folders,
              number_logs,
              number_questions,
              warn_long_paths
            }) => (
              <ListItem button key={operation_id} onClick={download(download_link)}>
                <ListItemIcon>
                  {stage === 100 ? download_link ? <GetAppIcon /> : <DoneIcon /> : <CircularProgress size={28}/>}
                </ListItemIcon>
                <ListItemText
                  primary={data_room_display_name}
                  secondary={
                    <>
                      <Typography variant='body1'>
                        {stage_label}
                      </Typography>
                      {number_folders ? <Typography variant="body2" className={classes.info}>
                        {`Number of folders: ${number_folders.toLocaleString()}`}
                      </Typography> : null}
                      {number_files ? <Typography variant="body2" className={classes.info}>
                        {`Number of files: ${number_files.toLocaleString()}`}
                      </Typography> : null}
                      {number_files_skipped ? <Typography variant="body2" className={classes.info}>
                        <i><b>Warning:</b></i> {`${number_files_skipped.toLocaleString()} file(s) were not copied because redaction is in progress or failed.`}
                      </Typography> : null}
                      {stage === 100 && number_files_sw ? <Typography variant="body2" className={classes.info}>
                        <i><b>Warning:</b></i> {`${number_files_sw.toLocaleString()} file(s) could not be included because they may only be opened in the Secure PDF Viewer.`}
                      </Typography> : null}
                      {stage === 100 && number_files_redacting ? <Typography variant="body2" className={classes.info}>
                        <i><b>Warning:</b></i> {`${number_files_redacting.toLocaleString()} file(s) could not be included because their redaction is still being applied. Try again once it has completed.`}
                      </Typography> : null}
                      {size ? <Typography variant="body2" className={classes.info}>
                        {`Total file size: ${toKb(size)} kB`}
                      </Typography> : null}
                      {size && size_dl && stage === 50 ? <Typography variant="body2" className={classes.info}>
                        {`Progress of current operation: ${toPct(Math.min(size_dl / size, 1))}`}
                      </Typography> : null}
                      {number_logs ? <Typography variant="body2" className={classes.info}>
                        {`Number of logs: ${number_logs.toLocaleString()}`}
                      </Typography> : null}
                      {number_questions ? <Typography variant="body2" className={classes.info}>
                        {`Number of questions: ${number_questions.toLocaleString()}`}
                      </Typography> : null}
                      {warn_long_paths ? <Typography variant="body2" className={classes.info}>
                        <i><b>Warning:</b></i> <span>The zip file contains long paths which may cause issues during extraction. Please see </span>
                        <a href="https://docs.aversure.com/using-the-service/opening-large-zip-files" target="_blank" rel="noreferrer" className={classes.warnLink} onClick={(e) => {e.stopPropagation()}}>how to open large zip files</a>.
                      </Typography> : null}
                    </>
                  }
                />
                {stage === 100 && (
                  <ListItemSecondaryAction>
                    <IconButton
                      edge='end'
                      size='small'
                      onClick={() =>
                        messageDispatch({
                          type: 'remove operation',
                          payload: {
                            operation_id,
                          },
                        })
                      }
                    >
                      <CancelIcon />
                    </IconButton>
                  </ListItemSecondaryAction>
                )}
              </ListItem>
            )
          )}
        </List>
      </div>
    )}
  </>);
}
